<template>
  <transition class="modal-wrapper" name="fade">
    <div v-if="visible">
      <div class="modal" :class="`${modalClass}`" @click.self="closeModal">
        <div
          class="backdrop"
          :class="`${modalClass}__backdrop`"
          @click="close(componentName)"
        />

        <div
          ref="modalContainer"
          class="modal-container"
          :class="`${modalClass}`"
          :style="{ top: top + 'px' }"
        >
          <div
            class="btn-close"
            :class="`${modalClass}`"
            @click="close(componentName, TrackEventsPopupSubtypes.CLOSE_TAPPED)"
          />

          <component :is="component" @close="close(componentName)" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import '@/assets/stylus/components/modal.styl'

import { computed, defineAsyncComponent, ref, watchEffect } from 'vue'
import { useStore } from 'vuex'

import { useModal } from '@/composables/useModal'
import { TrackEventsPopupSubtypes } from '@/constants'

import { ModalComponentName } from './types'

const store = useStore()

const modalContainer = ref<HTMLElement | null>(null)
const { top, close } = useModal(modalContainer)

const visible = computed<boolean>(() => store.getters['modal/isVisible'])
const modalClass = computed<string>(() => store.getters['modal/getModalClass'])

const componentName = computed(() => store.getters['modal/getComponent'])

const component = computed(() => {
  switch (componentName.value) {
    case ModalComponentName.REGISTRATION:
      return defineAsyncComponent(
        () => import('@/components/Form/Registration.vue')
      )
    case ModalComponentName.RESTORE:
      return defineAsyncComponent(() => import('@/components/Form/Restore.vue'))
    case ModalComponentName.INFO:
      return defineAsyncComponent(() => import('@/components/Form/Info.vue'))
    case ModalComponentName.NAVIGATION_OPTIONS:
      return defineAsyncComponent(
        () => import('@/pages/Library/NavigationOptions.vue')
      )
    case ModalComponentName.SUPPORT:
      return defineAsyncComponent(() => import('@/pages/Account/Support.vue'))
    case ModalComponentName.BANNER_ACCESS:
      return defineAsyncComponent(() => import('@/components/BannerAccess.vue'))
    case ModalComponentName.LANGUAGE_WARNING:
      return defineAsyncComponent(() => import('@/pages/Account/LanguageWarning.vue'))
    case ModalComponentName.WEB_INFO:
      return defineAsyncComponent(() => import('@/components/WebInfo.vue'))
    case ModalComponentName.PROMO:
      return defineAsyncComponent(() => import('@/components/Promo.vue'))
    case ModalComponentName.SUBSCRIPTION_SUCCESS:
      return defineAsyncComponent(
        () => import('@/components/SubscriptionSuccessPopup.vue')
      )
    case ModalComponentName.PROMO_NOT_REGISTERED:
      return defineAsyncComponent(
        () => import('@/components/PromoNotRegistered.vue')
      )
    case ModalComponentName.INSTALL_MOBILE_APP:
      return defineAsyncComponent(() => import('@/components/MobileApp.vue'))
    case ModalComponentName.GO_TO_MOBILE_APP:
      return defineAsyncComponent(
        () => import('@/components/Modal/GoToApp/GoToApp.vue')
      )
    case ModalComponentName.CLOSED_FOR_NOW:
      return defineAsyncComponent(() => import('@/components/ClosedForNow.vue'))
    case ModalComponentName.NOT_AVAILABLE:
      return defineAsyncComponent(
        () => import('@/components/Popups/NotAvailable.vue')
      )
    case ModalComponentName.AFTER_REPORT:
      return defineAsyncComponent(
        () => import('@/components/Popups/AfterReport.vue')
      )
    case ModalComponentName.COURSES_VIEW_CHANGED:
      return defineAsyncComponent(
        () => import('@/components/Popups/CoursesViewChanged.vue')
      )
    default:
      return null
  }
})

const closeModal = (): void => {
  if (componentName.value !== ModalComponentName.PROMO_NOT_REGISTERED) {
    return
  }

  close(ModalComponentName.PROMO_NOT_REGISTERED)
}

watchEffect(() => {
  document.body.style.overflow = visible.value ? 'hidden' : 'auto'
})
</script>
